import { useState } from 'react';
import { formatCurrency } from '../lib/format';

export interface HourlyPoint {
  hour: number;
  netSales: string;
  baskets: number;
}

/**
 * Trading through the day.
 *
 * Bars rather than a line: hours with no trade are genuinely empty, and a line
 * would draw a slope between 9am and 2pm implying the shop was gradually doing
 * something in between. A bar chart says "nothing happened here", which is the
 * truth and is often the interesting part.
 *
 * One measure, so one hue and no legend — the title names the series. Hours are
 * store-local; the API bucketed them with the store's timezone set on the
 * connection.
 */
const HOUR_TICKS = new Set([0, 6, 12, 18, 23]);

export function HourlyChart({ points }: { points: HourlyPoint[] }): React.JSX.Element {
  const [hovered, setHovered] = useState<number | null>(null);

  const max = Math.max(...points.map((point) => Number(point.netSales)), 0);
  const total = points.reduce((sum, point) => sum + Number(point.netSales), 0);

  if (total === 0) {
    return (
      <p className="px-3 py-8 text-center text-xs text-ink-faint">
        No trade recorded in this period.
      </p>
    );
  }

  const peak = points.reduce((best, point) =>
    Number(point.netSales) > Number(best.netSales) ? point : best,
  );

  return (
    <div className="px-3 pt-3 pb-2">
      <div
        className="relative flex h-28 items-end gap-[2px]"
        role="img"
        aria-label={`Net sales by hour. Busiest hour ${String(peak.hour).padStart(2, '0')}:00 with ${formatCurrency(peak.netSales)}.`}
        onMouseLeave={() => setHovered(null)}
      >
        {points.map((point) => {
          const value = Number(point.netSales);
          // Negative hours happen when a return outweighs the hour's takings.
          const height = max > 0 ? Math.max((Math.abs(value) / max) * 100, value === 0 ? 0 : 2) : 0;
          const isHovered = hovered === point.hour;

          return (
            <button
              key={point.hour}
              type="button"
              className="group relative flex h-full flex-1 cursor-default items-end"
              onMouseEnter={() => setHovered(point.hour)}
              onFocus={() => setHovered(point.hour)}
              onBlur={() => setHovered(null)}
              aria-label={`${String(point.hour).padStart(2, '0')}:00, ${formatCurrency(point.netSales)}, ${point.baskets} baskets`}
            >
              {/* Hit target spans the full column height, so the pointer does not
                  have to find a 3px bar. */}
              <span
                className={
                  value < 0
                    ? 'w-full rounded-t-[3px] bg-down transition-opacity'
                    : 'w-full rounded-t-[3px] bg-accent transition-opacity'
                }
                style={{ height: `${height}%`, opacity: isHovered ? 1 : 0.85 }}
              />
            </button>
          );
        })}

        {hovered !== null && (
          <Tooltip point={points[hovered]!} hourCount={points.length} />
        )}
      </div>

      {/* Labels reuse the bars' own layout — same flex, same gap — so each one
          is centred under its column by construction. Spacing them with
          justify-between instead puts "06" a couple of percent to the left of
          the bar it names, which is exactly the kind of small lie a chart
          should not tell. */}
      <div className="mt-1.5 flex gap-[2px]">
        {points.map((point) => (
          <span
            key={point.hour}
            className="qs-figure flex-1 text-center text-2xs text-ink-faint"
            aria-hidden
          >
            {HOUR_TICKS.has(point.hour) ? String(point.hour).padStart(2, '0') : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

function Tooltip({
  point,
  hourCount,
}: {
  point: HourlyPoint;
  hourCount: number;
}): React.JSX.Element {
  // Flip to the left half once past the midpoint so the tooltip never runs off
  // the panel on the evening hours.
  const pastMiddle = point.hour > hourCount / 2;

  return (
    <div
      className="pointer-events-none absolute -top-1 z-10 rounded border border-rule bg-surface px-2 py-1 shadow-sm"
      style={{
        // Anchored to the centre of the column, matching the axis labels.
        left: `${((point.hour + 0.5) / hourCount) * 100}%`,
        transform: pastMiddle ? 'translateX(-100%)' : undefined,
      }}
    >
      <p className="qs-figure text-2xs text-ink-faint">
        {String(point.hour).padStart(2, '0')}:00
      </p>
      <p className="qs-figure text-xs font-medium">{formatCurrency(point.netSales)}</p>
      <p className="text-2xs text-ink-muted">
        {point.baskets} basket{point.baskets === 1 ? '' : 's'}
      </p>
    </div>
  );
}
